import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Avatar,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { messagingService } from '../../services/api/messaging.service';

export interface OrgUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface UserSearchPickerProps {
  onSelectUsers: (users: OrgUser[]) => void;
  excludeUserIds?: string[];
  multiple?: boolean;
  label?: string;
  placeholder?: string;
}

const UserSearchPicker: React.FC<UserSearchPickerProps> = ({
  onSelectUsers,
  excludeUserIds = [],
  multiple = true,
  label = 'Add people',
  placeholder = 'Search by name or email...',
}) => {
  const [options, setOptions] = useState<OrgUser[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<OrgUser[]>([]);

  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const results = await messagingService.searchOrgUsers(query);
      const filtered = results.filter(
        (u: OrgUser) => !excludeUserIds.includes(u.id)
      );
      setOptions(filtered);
    } catch (error) {
      console.error('Failed to search users:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [excludeUserIds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, searchUsers]);

  const getDisplayName = (user: OrgUser) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };

  const getInitials = (user: OrgUser) => {
    const name = getDisplayName(user);
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => {
        const users = Array.isArray(value) ? value : value ? [value] : [];
        setSelected(users as OrgUser[]);
        onSelectUsers(users as OrgUser[]);
      }}
      getOptionLabel={(option) => getDisplayName(option)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x} // Disable client-side filtering, server handles it
      renderOption={(props, option) => (
        <li {...props} key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar style={{ width: 28, height: 28, fontSize: 12 }}>
            {getInitials(option)}
          </Avatar>
          <div>
            <Typography variant="body2">{getDisplayName(option)}</Typography>
            {(option.first_name || option.last_name) && (
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            )}
          </div>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            avatar={<Avatar style={{ width: 20, height: 20, fontSize: 10 }}>{getInitials(option)}</Avatar>}
            label={getDisplayName(option)}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selected.length === 0 ? placeholder : ''}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {selected.length === 0 && <PersonAddIcon fontSize="small" style={{ color: 'rgba(0,0,0,0.6)', marginRight: 4 }} />}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={inputValue.length < 2 ? 'Type to search...' : 'No users found'}
    />
  );
};

export default UserSearchPicker;
